import { Injectable } from '@angular/core';
import { Organization } from './organization.dto';
import { DIVISIONS_COLLECTION, ID_FIELD_OPTIONS, NO_LIMIT_SEARCH_PARAMS, ORGANIZATIONS_COLLECTION, Query, SearchResult } from '@damofire/data-commons';
import { DivisionRole, OrganizationLink } from '@damofire/data-security';
import { firstValueFrom, Observable } from 'rxjs';
import { doc, docData, getFirestore } from '@angular/fire/firestore';
import { Division } from './division.dto';
import { FirebaseObservableService, FirebaseObservableServiceFactory } from '@damofire/data-angular-commons';

export function getOrganizationById(id: string): Observable<Organization> {
  return docData<Organization>(doc(getFirestore(), `${ORGANIZATIONS_COLLECTION}/${id}`), ID_FIELD_OPTIONS);
}

@Injectable()
export abstract class OrganizationService {
  abstract search(query: Query): Promise<SearchResult<Organization>>;
  abstract observeSearch(query: Query): Observable<SearchResult<Organization>>;
  abstract divisionLinkMerge(links: DivisionRole[], adminOrgs: OrganizationLink[]): Promise<void>;
}

@Injectable()
export class FirebaseOrganizationService implements OrganizationService {
  private readonly service: FirebaseObservableService<Organization>;

  constructor(readonly firebaseObservableServiceFactory: FirebaseObservableServiceFactory) {
    this.service = firebaseObservableServiceFactory.createService(ORGANIZATIONS_COLLECTION);
  }

  search(q: Query): Promise<SearchResult<Organization>> {
    return firstValueFrom(this.observeSearch(q));
  }

  observeSearch(q: Query): Observable<SearchResult<Organization>> {
    return this.service.observeCollectionSearch(q, ['name', 'principal.name']);
  }

  // Merges the required fields from adminOrgs into the DivisionRole links
  async divisionLinkMerge(links: DivisionRole[], adminOrgs: OrganizationLink[]): Promise<void> {
    if (adminOrgs?.length) {
      // Get orgIds to search for within divisions
      const orgIds = adminOrgs.map((adminOrgLink) => adminOrgLink._id);
      const orgDivisions = await this.service.collection<Division>(DIVISIONS_COLLECTION, {
        ...NO_LIMIT_SEARCH_PARAMS,
        filters: { 'organization._id': orgIds },
        sort: { name: 'asc' },
      });

      this.mergeLinks(orgDivisions, links);
    }
  }

  private mergeLinks(orgDivisions: SearchResult<Division>, links: DivisionRole[]): void {
    // Loop through adminOrgs->divisions and see if they exist in the divisionRole links, if not push, otherwise update.
    orgDivisions.result.forEach((division) => {
      const matchingOrgLink = links.findIndex((link: DivisionRole) => link._id === division._id);
      const divisionRole: DivisionRole = {
        orgId: division.organization._id,
        orgName: division.organization.name,
        _id: division._id,
        name: division.name,
        enabled: !!division.enabled,
      };

      if (matchingOrgLink === -1) {
        links.push(divisionRole);
      } else {
        links[matchingOrgLink] = { ...links[matchingOrgLink], ...divisionRole };
      }
    });
  }
}
